/*
 * Bundle components, read live from the Storefront API (docs/15 15.10). Built 24.09.2026
 * next to the mirrored bundle_position entries, the only source since they are retired
 * (E23, docs/09 9.2.10).
 *
 * WHY CLIENT SIDE AT ALL. Liquid knows nothing about a bundle outside the cart, measured
 * again on 23.09.2026. The Storefront API does, and it also returns a component that is
 * unpublished from every channel -- through the bundle only; asked for directly the same
 * product is null. That is exactly the free extra the row has to show (M1, 24.09.2026).
 *
 * Three parts, kept apart on purpose:
 *
 *   ladeBundle()   data: one query, normalised into plain numbers and strings.
 *   zeilenpreise() money: the display rule from the design excerpt. Pure, no DOM, so it
 *                  can be checked in node against the figures of the excerpt.
 *   <mo-bundle-live>  rendering, into Digital's own card and price markup.
 *
 * Stage 2 (branded ingredients and claims per component) is <mo-rohstoffe-live> at the end:
 * it takes the component handles from ladeBundle() and lets Liquid render the rest through
 * the Section Rendering API, so it needs no token either. Tokenless, the Storefront API
 * refuses metafields outright (unauthenticated_read_metafields, measured 24.09.2026).
 *
 * All amounts are integer cents. The API answers "34.9", and floating point is not
 * allowed anywhere near a price.
 */

const KOMPONENTEN_QUERY = `query MoBundle($handle: String!) {
  product(handle: $handle) {
    handle
    variants(first: 1) { nodes {
      id
      price { amount currencyCode }
      components(first: 20) { nodes {
        quantity
        productVariant {
          id
          title
          price { amount }
          compareAtPrice { amount }
          image { url altText }
          product {
            handle
            title
            description
            featuredImage { url altText }
            variants(first: 2) { nodes { id } }
          }
        }
      } }
    } }
  }
}`;

/* The same card size and description length as the Liquid list shape, so both render alike. */
const KARTE_BILD_BREITE = 600;
const KARTE_BILD_HOEHE = 686;
const BESCHREIBUNG_WOERTER = 24;

export class BundleLadefehler extends Error {}

const cent = (money) => (money ? Math.round(Number(money.amount) * 100) : null);

/* Which of the given product handles the storefront can see on their own. A component
 * unpublished from the Online Store comes back through the bundle but is null when asked
 * for directly (M1, 24.09.2026) -- and that is the only reliable signal: onlineStoreUrl is
 * null for EVERY product while the storefront password is on, published or not (measured
 * 24.09.2026). A card for a hidden product gets no link, its page would 404. */
async function sichtbareHandles(handles, anfrage) {
  if (!handles.length) return new Set();
  const felder = handles.map((h, i) => `p${i}: product(handle: ${JSON.stringify(h)}) { handle }`);
  const data = await anfrage(`{ ${felder.join(' ')} }`, {});
  return new Set(Object.values(data).filter(Boolean).map((p) => p.handle));
}

/**
 * Loads one bundle and returns it normalised. Throws BundleLadefehler on anything that is
 * not a usable answer, including GraphQL errors: the API answers access problems with
 * HTTP 200 and an `errors` array, and a missing scope on a nested field arrives as a
 * silent null -- measured 24.09.2026 -- so an empty result is never taken at face value.
 */
export async function ladeBundle(handle, { apiVersion, token, routesRoot = '/' } = {}) {
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (token) headers['X-Shopify-Storefront-Access-Token'] = token;

  const anfrage = async (query, variables) => {
    const antwort = await fetch(`/api/${apiVersion}/graphql.json`, {
      method: 'POST', headers, body: JSON.stringify({ query, variables }),
    });
    if (!antwort.ok) throw new BundleLadefehler(`HTTP ${antwort.status} for bundle ${handle}`);
    const { data, errors } = await antwort.json();
    if (errors?.length) throw new BundleLadefehler(errors.map((e) => e.message).join('; '));
    return data;
  };

  const data = await anfrage(KOMPONENTEN_QUERY, { handle });
  const variante = data?.product?.variants?.nodes?.[0];
  if (!variante) throw new BundleLadefehler(`no product or variant for ${handle}`);

  const komponenten = variante.components.nodes;
  const sichtbar = await sichtbareHandles(
    [...new Set(komponenten.map((k) => k.productVariant.product.handle))], anfrage,
  );

  return {
    handle,
    variantId: variante.id,
    preis: cent(variante.price),
    waehrung: variante.price.currencyCode,
    positionen: komponenten.map((k) => {
      const v = k.productVariant;
      return {
        handle: v.product.handle,
        sichtbar: sichtbar.has(v.product.handle),
        titel: v.product.title,
        variantenId: v.id.split('/').pop(),
        variantenTitel: v.title,
        // The storefront lists only published variants: a hidden component answers with none
        // at all (measured 01.10.2026), a hidden free variant beside a visible one with just
        // that other one (Testofen, 27.10.2026). Any other visible variant means a choice.
        einzigeVariante: !v.product.variants.nodes.some((n) => n.id !== v.id),
        url: sichtbar.has(v.product.handle) ? `${routesRoot}products/${v.product.handle}` : null,
        bild: v.image ?? v.product.featuredImage,
        beschreibung: v.product.description,
        menge: Math.max(1, k.quantity),
        preis: cent(v.price),
        nennwert: cent(v.compareAtPrice),
      };
    }),
  };
}

/**
 * The row prices of the design excerpt "180 Tage System Bundle" (docs/15 15.10):
 *
 *   free rows       price 0          -> 0, struck: the nominal value (compare-at x qty)
 *   the first priced row             -> full price
 *   the other priced rows            -> carry the whole discount, split by their value,
 *                                       the last one takes the rounding remainder
 *   total struck                     -> sum of every row at its normal value, the free
 *                                       rows at their nominal value
 *
 * A DISPLAY rule, not Shopify's price: the cart splits the discount proportionally over
 * all rows (docs/04 4.12). The rows still add up to the bundle price exactly.
 *
 * OPEN, and the reason this is one replaceable function: where the discount goes with
 * more than two priced rows is MOs call. The excerpt shows only two. When the discount
 * exceeds what the other rows are worth, the first row gives up the rest.
 */
export function zeilenpreise(positionen, bundlepreis) {
  const zeilen = positionen.map((p) => {
    const normal = p.preis * p.menge;
    const nominal = (p.nennwert ?? 0) * p.menge;
    return { ...p, normal, nominal, gratis: p.preis === 0, anzeige: normal };
  });

  const bepreist = zeilen.filter((z) => !z.gratis);
  const rabatt = bepreist.reduce((s, z) => s + z.normal, 0) - bundlepreis;

  if (rabatt > 0 && bepreist.length === 1) {
    bepreist[0].anzeige -= rabatt;
  } else if (rabatt > 0 && bepreist.length > 1) {
    const [erste, ...uebrige] = bepreist;
    const wertUebrige = uebrige.reduce((s, z) => s + z.normal, 0);
    const aufUebrige = Math.min(rabatt, wertUebrige);
    let verteilt = 0;
    uebrige.forEach((z, i) => {
      const teil = i === uebrige.length - 1
        ? aufUebrige - verteilt
        : Math.round((aufUebrige * z.normal) / wertUebrige);
      z.anzeige -= teil;
      verteilt += teil;
    });
    erste.anzeige -= rabatt - aufUebrige;
  }

  for (const z of zeilen) {
    const vergleich = Math.max(z.normal, z.nominal);
    z.streich = vergleich > z.anzeige ? vergleich : null;
  }
  const streichGesamt = zeilen.reduce((s, z) => s + Math.max(z.normal, z.nominal), 0);

  return { zeilen, streichGesamt: streichGesamt > bundlepreis ? streichGesamt : null };
}

/* ------------------------------------------------------------------ rendering */

const el = (tag, klassen, text) => {
  const e = document.createElement(tag);
  if (klassen) e.className = klassen;
  if (text !== undefined) e.textContent = text;
  return e;
};

const kuerzen = (text, woerter) => {
  const teile = (text || '').trim().split(/\s+/).filter(Boolean);
  return teile.length > woerter ? `${teile.slice(0, woerter).join(' ')}…` : teile.join(' ');
};

/* Same contract as snippets/mo-bundle-preis.liquid: both branches carry the amount and
 * .price--on-sale picks one. Rendering only one branch prints nothing at all. */
function preisBlock(text, streichText) {
  const wrap = el('div', `price mo-bundle__price${streichText ? ' price--on-sale' : ''}`);
  const dl = el('dl');
  const regular = el('div', 'price__regular');
  const regDd = el('dd', 'price__last');
  regDd.append(el('span', 'price-item price-item--regular', text));
  regular.append(regDd);
  const sale = el('div', 'price__sale');
  const saleDd = el('dd', 'price__last');
  saleDd.append(el('span', 'price-item price-item--sale', text));
  const cmpDd = el('dd', 'price__compare');
  cmpDd.append(el('s', 'price-item price-item--regular', streichText || ''));
  sale.append(saleDd, cmpDd);
  dl.append(regular, sale);
  wrap.append(dl);
  return wrap;
}

function bildUrl(bild, breite) {
  if (!bild?.url) return null;
  const url = new URL(bild.url);
  url.searchParams.set('width', String(breite));
  return url.toString();
}

/* The variant title without its bracketed part, appended to the product title on a card:
 * "Refill (1 Monat)" -> "Shilajit Complex Refill" (decided 01.10.2026, USP). For every
 * variant of a product that HAS a choice: a rule keyed on a word ("Original") or on the
 * variant order would not survive MOs free maintenance. A product with a single variant has
 * nothing to tell apart, whatever that variant is called ("Standard" on the free extras). */
const VARIANTE_OHNE_TITEL = 'Default Title';

function kartentitel(zeile) {
  if (zeile.einzigeVariante || !zeile.variantenTitel || zeile.variantenTitel === VARIANTE_OHNE_TITEL) return zeile.titel;
  const zusatz = zeile.variantenTitel.replace(/\s*\([^)]*\)\s*$/, '').trim();
  return zusatz ? `${zeile.titel} ${zusatz}` : zeile.titel;
}

/* collection-product-card and quickview--hover are Digital's hover zoom, the same pair the
 * Liquid list puts on a card that links; a hidden component has no link and gets neither. */
function karte(zeile, geld, texte, hoverZoom) {
  const li = el('li', [
    'card-wrapper',
    zeile.url ? 'collection-product-card' : '',
    zeile.url && hoverZoom ? 'quickview--hover' : '',
    'mo-bundle__component',
    zeile.gratis ? 'mo-bundle__component--free' : '',
    zeile.url ? '' : 'mo-bundle__component--hidden',
  ].filter(Boolean).join(' '));

  const card = el('div', 'card card--product');
  const inner = el('div', 'card__inner');
  const media = el('div', 'media media--transparent media--hover-effect');
  const src = bildUrl(zeile.bild, KARTE_BILD_BREITE);
  if (src) {
    const img = el('img');
    Object.assign(img, { src, alt: zeile.bild.altText || zeile.titel, width: KARTE_BILD_BREITE, height: KARTE_BILD_HOEHE, loading: 'lazy' });
    media.append(img);
  }
  inner.append(media);
  card.append(inner);

  const info = el('div', 'card-information');
  const wrapper = el('div', 'card-information__wrapper');
  const h3 = el('h3', 'card__title h5');
  if (zeile.url) {
    const a = el('a', 'full-unstyled-link', kartentitel(zeile));
    a.href = zeile.url;
    h3.append(a);
  } else {
    h3.textContent = kartentitel(zeile);
  }
  wrapper.append(h3);

  const beschreibung = kuerzen(zeile.beschreibung, BESCHREIBUNG_WOERTER);
  if (beschreibung) wrapper.append(el('p', 'mo-bundle__description', beschreibung));

  const fuss = el('div', 'mo-bundle__foot');
  fuss.append(el('p', 'mo-bundle__quantity', ersetze(texte.anzahl, { n: zeile.menge })));
  const preisZeile = el('div', 'card__price-row');
  preisZeile.append(preisBlock(
    zeile.gratis ? texte.gratis : geld(zeile.anzeige),
    zeile.streich ? geld(zeile.streich) : null,
  ));
  fuss.append(preisZeile);
  wrapper.append(fuss);
  info.append(wrapper);
  li.append(card, info);
  if (zeile.gratis) li.append(el('span', 'mo-bundle__badge', texte.gratis_badge));
  return li;
}

/* The fill quantity is a metafield, which the tokenless Storefront API refuses; Liquid renders
 * it through sections/mo-fuellmenge.liquid instead. A hidden component has no product page
 * to render from and gets none, which is fine: the free extras carry no fill quantity. */
async function fuellmengeLaden(zeile, karteEl, texte, routesRoot) {
  if (!zeile.url) return;
  const parameter = new URLSearchParams({ variant: zeile.variantenId, section_id: 'mo-fuellmenge' });
  const antwort = await fetch(`${routesRoot}products/${encodeURIComponent(zeile.handle)}?${parameter}`);
  if (!antwort.ok) throw new BundleLadefehler(`HTTP ${antwort.status} for the fill quantity of ${zeile.handle}`);
  const html = new DOMParser().parseFromString(await antwort.text(), 'text/html');
  const text = html.querySelector('[data-mo-fuellmenge]')?.textContent.trim();
  if (!text) return;
  const menge = el('span', 'card__amount', zeile.menge > 1 ? ersetze(texte.fuellmenge_je, { f: text }) : text);
  karteEl.querySelector('.card__price-row').append(menge);
}

/* The empty state of a failed request, written next to the target. Only here and never in
 * the HTML: there it was the one text a crawler without JavaScript read (docs/15 15.10). */
function fehlerZeigen(ziel, klasse, text) {
  if (!ziel || !text || ziel.parentElement.querySelector(`.${klasse}`)) return;
  ziel.after(el('p', `mo-hinweis ${klasse}`, text));
}

// Guarded, so node can import this module to check zeilenpreise() without a DOM.
if (typeof HTMLElement !== 'undefined' && !customElements.get('mo-bundle-live')) {
  class MoBundleLive extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.moStatus) return;
      this.dataset.moStatus = 'laedt';

      const texte = JSON.parse(this.dataset.texte || '{}');
      const geld = (cents) => new Intl.NumberFormat(this.dataset.locale || 'de-DE', {
        style: 'currency', currency: this.dataset.currency || 'EUR',
      }).format(cents / 100);

      try {
        const bundle = await ladeBundle(this.dataset.handle, {
          apiVersion: this.dataset.api, token: this.dataset.token,
        routesRoot: window.Shopify?.routes?.root ?? '/',
        });
        if (!bundle.positionen.length) throw new BundleLadefehler('bundle has no components');

        const { zeilen, streichGesamt } = zeilenpreise(bundle.positionen, bundle.preis);
        const liste = this.querySelector('[data-mo-ziel]');
        const karten = zeilen.map((z) => karte(z, geld, texte, this.dataset.hoverZoom === 'true'));
        liste.replaceChildren(...karten);
        zeilen.forEach((z, i) => fuellmengeLaden(z, karten[i], texte, window.Shopify?.routes?.root ?? '/')
          .catch((fehler) => console.warn('[mo-bundle-live]', this.dataset.handle, fehler)));

        if (streichGesamt) {
          this.querySelectorAll('[data-mo-streich-gesamt]').forEach((e) => { e.textContent = geld(streichGesamt); });
          this.querySelectorAll('[data-mo-streich-gesamt], [data-mo-streich-hinweis]').forEach((e) => { e.hidden = false; });
        }
        this.dataset.moStatus = 'fertig';
      } catch (fehler) {
        // Visible to the visitor as the defined empty state, and loud in the console: a
        // silent empty block is the failure mode this project keeps running into.
        console.warn('[mo-bundle-live]', this.dataset.handle, fehler);
        this.dataset.moStatus = 'fehler';
        fehlerZeigen(this.querySelector('[data-mo-ziel]'), 'mo-bundle-live__leer', texte.fehler);
      }
    }
  }

  customElements.define('mo-bundle-live', MoBundleLive);
}

/*
 * STAGE 2, 24.09.2026: the branded ingredients, or the ingredient declaration, of the
 * bundle page (docs/15 15.10). The placeholder comes from sections/mo-branded-ingredients.liquid
 * in its live mode and names every tier of the page; it already holds the frame.
 *
 * Nothing is rendered here. The script asks the very section it sits in to render itself
 * for each component (Section Rendering API, ?section_id=<its id>&view=<its template>),
 * and moves the finished pieces over. So the markup, the settings of the template and
 * the claims all stay Liquid's; there is no second renderer to keep in step.
 *
 * Components hidden from the Online Store are skipped: their product URL 404s and the
 * fragment comes back as an empty shell (measured 24.09.2026). A product shared by two
 * tiers is fetched once. Original and refill of one product count as one.
 *
 * ALL PRODUCTS OF ALL TIERS, independent of the tier picked above (MO-125, decided
 * 01.10.2026): the branded ingredients get one tab per product, below the section heading
 * in the right column, each panel the product's own texts (where maintained) and rows; the
 * pictures of every product stack in the left column. The declaration gets one accordion
 * row per product, and its nutrition facts pictures stack in the left column the same way
 * (06.10.2026). Order is tier order, then component order.
 *
 * NOTHING JUMPS: pictures and panels are stacked in one grid cell each and switched by
 * visibility, so both columns keep the height of their tallest member.
 */
const fragmente = new Map();

/* One request per bundle and page: the raw materials and both stage shapes of a tier ask
 * for the same bundle. A failed request is not cached. */
const bundles = new Map();

function ladeBundleEinmal(handle, optionen) {
  if (!bundles.has(handle)) {
    const antwort = ladeBundle(handle, optionen);
    antwort.catch(() => bundles.delete(handle));
    bundles.set(handle, antwort);
  }
  return bundles.get(handle);
}

function ladeFragment(handle, { sectionId, view, routesRoot }) {
  const schluessel = `${handle}|${sectionId}|${view}`;
  if (!fragmente.has(schluessel)) {
    const parameter = new URLSearchParams({ section_id: sectionId });
    if (view) parameter.set('view', view);
    const antwort = fetch(`${routesRoot}products/${encodeURIComponent(handle)}?${parameter}`)
      .then(async (r) => {
        if (!r.ok) throw new BundleLadefehler(`HTTP ${r.status} for the fragment of ${handle}`);
        return r.text();
      });
    // A failed request is not cached, the next tier may try again.
    antwort.catch(() => fragmente.delete(schluessel));
    fragmente.set(schluessel, antwort);
  }
  return fragmente.get(schluessel);
}

/* The pieces of one fragment, plus its <style>: the generated rules that pair a row with
 * its picture are keyed on the section id, which is the same on this page, and scoped to
 * one element. A placeholder in the fragment would mean the section did NOT switch to its
 * component mode -- the failure measured before this was built -- so it is refused, loudly.
 * A product without anything to show comes back without either piece. */
function teileAus(html, handle) {
  const vorlage = document.createElement('template');
  vorlage.innerHTML = html;
  const inhalt = vorlage.content;
  if (inhalt.querySelector('mo-rohstoffe-live')) {
    throw new BundleLadefehler(`the fragment of ${handle} carries the bundle page placeholder`);
  }
  return {
    handle,
    stile: [...inhalt.querySelectorAll('style')],
    bilder: [...inhalt.querySelectorAll('.mo-branded-ingredients__image')],
    panel: inhalt.querySelector('.mo-rohstoffe-panel'),
    zeilen: inhalt.querySelector('.mo-branded-ingredients--zutaten .collapsible-content__content'),
  };
}

/* Digital binds its accordion once, on the toggles present at page load
 * (assets/collapsible-content.js), and rebinds on shopify:section:load. That event is not
 * raised here: about forty listeners in the theme react to it, header and popup included.
 * The same handler is bound to the new toggles instead, with the same off/on pair, so a
 * later rebind by Digital replaces it rather than doubling it. */
function bindeAkkordeons(wurzel) {
  const $ = window.jQuery;
  if (!$) {
    console.warn('[mo-rohstoffe-live] jQuery missing, the accordions stay as rendered');
    return;
  }
  $(wurzel).find('.collapsible-content__toggle').off('click').on('click', function () {
    const parent = $(this).parent();
    const answer = $(this).next('.collapsible-content__answer');
    if (!parent.hasClass('active')) {
      parent.siblings('.collapsible-content__item.active').removeClass('active')
        .find('.collapsible-content__answer').stop()
        .slideUp(300, function () { $(this).css('display', 'none'); });
      parent.addClass('active');
      answer.stop().slideDown(300, function () { $(this).css('display', 'block'); });
    } else {
      parent.removeClass('active');
      answer.stop().slideUp(300, function () { $(this).css('display', 'none'); });
    }
  });
}

let reiterZaehler = 0;

/* WAI-ARIA tabs: one tab stop, arrows move and activate, Home and End jump. An inactive
 * panel stays in the stack, out of sight and out of reach (data-mo-inaktiv for the CSS and
 * the picture pairing, inert for focus and assistive technology). */
function reiterBauen(panels, beschriftungen, leistenName) {
  const leiste = el('div', 'mo-rohstoffe-reiter');
  leiste.setAttribute('role', 'tablist');
  if (leistenName) leiste.setAttribute('aria-label', leistenName);
  const basis = `mo-reiter-${(reiterZaehler += 1)}`;

  const tabs = panels.map((panel, i) => {
    const tab = el('button', 'mo-rohstoffe-reiter__tab', beschriftungen[i]);
    Object.assign(tab, { type: 'button', id: `${basis}-tab-${i}` });
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-controls', `${basis}-panel-${i}`);
    Object.assign(panel, { id: `${basis}-panel-${i}`, tabIndex: 0 });
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', tab.id);
    return tab;
  });

  const waehle = (index, fokus) => {
    tabs.forEach((tab, i) => {
      const aktiv = i === index;
      tab.setAttribute('aria-selected', String(aktiv));
      tab.tabIndex = aktiv ? 0 : -1;
      panels[i].toggleAttribute('data-mo-inaktiv', !aktiv);
      panels[i].inert = !aktiv;
    });
    if (fokus) tabs[index].focus();
  };

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => waehle(i, false));
    tab.addEventListener('keydown', (ereignis) => {
      const ziel = {
        ArrowRight: (i + 1) % tabs.length,
        ArrowLeft: (i - 1 + tabs.length) % tabs.length,
        Home: 0,
        End: tabs.length - 1,
      }[ereignis.key];
      if (ziel === undefined) return;
      ereignis.preventDefault();
      waehle(ziel, true);
    });
  });

  leiste.append(...tabs);
  waehle(0, false);
  return leiste;
}

/* The declaration: one product is just its text, several are one accordion row each, in
 * one list. Only the first row keeps the open state Liquid gave every fragment's row. */
function zutatenZusammensetzen(teile) {
  if (teile.length === 1) {
    return [...teile[0].zeilen.querySelector('.collapsible-content__answer-inner').children];
  }
  const [erster, ...weitere] = teile;
  const liste = erster.zeilen.querySelector('.collapsible-content__items');
  for (const teil of weitere) {
    for (const zeile of teil.zeilen.querySelectorAll('.collapsible-content__item')) {
      zeile.classList.remove('active');
      zeile.querySelector('.collapsible-content__answer')?.style.removeProperty('display');
      liste.append(zeile);
    }
  }
  return [erster.zeilen];
}

function stufenDerStage() {
  const handles = [...document.querySelectorAll('[data-mo-stufe-produkt]')]
    .map((e) => e.dataset.moStufeProdukt)
    .filter(Boolean);
  return [...new Set(handles)];
}

/* The visible products of all tiers that loaded, each once, in tier order. A tier that
 * failed is reported and left out; the page still shows the others. */
async function produkteDerStufen(handles, optionen) {
  const ergebnisse = await Promise.allSettled(handles.map((h) => ladeBundleEinmal(h, optionen)));
  ergebnisse.forEach((e, i) => {
    if (e.status === 'rejected') console.warn('[mo-rohstoffe-live]', handles[i], e.reason);
  });
  const geladen = ergebnisse.filter((e) => e.status === 'fulfilled').map((e) => e.value);
  if (!geladen.length) throw new BundleLadefehler(`no tier could be loaded: ${handles.join(', ')}`);
  const titel = new Map();
  for (const p of geladen.flatMap((b) => b.positionen)) {
    if (p.sichtbar && !titel.has(p.handle)) titel.set(p.handle, p.titel);
  }
  return titel;
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('mo-rohstoffe-live')) {
  class MoRohstoffeLive extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.moStatus) return;
      this.dataset.moStatus = 'laedt';
      const routesRoot = window.Shopify?.routes?.root ?? '/';
      const ziel = this.querySelector('[data-mo-ziel]');
      const medien = this.querySelector('[data-mo-medien]');
      const zutaten = this.dataset.inhalt === 'zutaten';

      const stufen = stufenDerStage();
      if (!stufen.length) {
        // A product page whose product carries nothing for this section: no stage, no bundle.
        this.dataset.moStatus = 'leer';
        return;
      }

      try {
        const produkte = await produkteDerStufen(stufen, {
          apiVersion: this.dataset.api, token: this.dataset.token, routesRoot,
        });
        const handles = [...produkte.keys()];
        const antworten = await Promise.all(handles.map((h) => ladeFragment(h, {
          sectionId: this.dataset.section, view: this.dataset.view, routesRoot,
        })));

        const teile = antworten.map((html, i) => teileAus(html, handles[i]))
          .filter((t) => (zutaten ? t.zeilen : t.panel));
        if (!teile.length) {
          // Nothing to show at all: no heading standing alone either.
          medien?.remove();
          this.dataset.moStatus = 'leer';
          return;
        }

        const stile = teile.flatMap((t) => t.stile);
        if (zutaten) {
          ziel.replaceChildren(...stile, ...zutatenZusammensetzen(teile));
        } else {
          const panels = teile.map((t) => t.panel);
          const reiter = panels.length > 1
            ? [reiterBauen(panels, teile.map((t) => produkte.get(t.handle)), this.dataset.reiterLabel)]
            : [];
          const stapel = el('div', 'mo-rohstoffe-panels');
          stapel.append(...panels);
          ziel.replaceChildren(...stile, ...reiter, stapel);
        }
        const bilder = teile.flatMap((t) => t.bilder);
        if (bilder.length) medien?.replaceChildren(...bilder);
        else medien?.remove();
        bindeAkkordeons(ziel);
        ziel.removeAttribute('aria-busy');
        this.dataset.moStatus = 'fertig';
      } catch (fehler) {
        console.warn('[mo-rohstoffe-live]', stufen, fehler);
        this.dataset.moStatus = 'fehler';
        medien?.remove();
        fehlerZeigen(ziel, 'mo-rohstoffe-live__leer', this.dataset.fehler);
      }
    }
  }

  customElements.define('mo-rohstoffe-live', MoRohstoffeLive);
}

/*
 * STAGE SHAPES, 24.09.2026: `bilder` and `kompakt` of snippets/mo-bundle-bestandteile
 * (docs/15 15.10), filled from ladeBundle().
 *
 * What stays Liquid: the bundle price and the bundle's own pack line. The lines are the
 * component names -- the pack of a component is a metafield, and the Storefront API gives no
 * metafield without a token. Decided 24.09.2026, USP.
 *
 * The struck total is zeilenpreise()'s, the figure the teaser's buy bar shows as well, so
 * both places agree by construction.
 */
const ersetze = (vorlage, werte) =>
  Object.entries(werte).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, String(v)), vorlage || '');

function bilderFuellen(ziel, positionen, texte) {
  const eintraege = positionen.map((p, i) => {
    // The first one is shown large in the middle of the stage.
    const breite = i === 0 ? 1200 : 400;
    const src = bildUrl(p.bild, breite);
    if (!src) return null;
    const gratis = p.preis === 0;
    const li = el('li', `mo-bundle__visual${gratis ? ' mo-bundle__visual--free' : ''}`);
    const img = el('img', 'mo-bundle__visual-image');
    Object.assign(img, { src, alt: p.titel, width: breite, height: breite, loading: 'lazy' });
    li.append(img);
    if (gratis) li.append(el('span', 'mo-bundle__badge', texte.gratis_badge));
    if (p.menge > 1) {
      li.append(el('span', 'mo-bundle__visual-quantity', `${ersetze(texte.menge, { n: p.menge })} ${p.titel}`));
    }
    return li;
  });
  // Without a single picture the bundle's own image stays, rendered server side.
  if (eintraege.some(Boolean)) ziel.replaceChildren(...eintraege.filter(Boolean));
}

function kompaktFuellen(wurzel, bundle, texte, geld) {
  const zeilen = wurzel.querySelector('[data-mo-zeilen]');
  if (zeilen && !zeilen.children.length) {
    zeilen.replaceChildren(...bundle.positionen.filter((p) => p.preis !== 0).map((p) => {
      const li = el('li', 'mo-bundle__line');
      const name = p.menge > 1 ? `${ersetze(texte.menge, { n: p.menge })} ${kartentitel(p)}` : kartentitel(p);
      li.append(el('span', 'mo-bundle__line-name', name));
      return li;
    }));
  }

  const { streichGesamt } = zeilenpreise(bundle.positionen, bundle.preis);
  const preis = wurzel.querySelector('.mo-bundle__price');
  if (preis && streichGesamt) {
    // The sale branch arrives empty when Liquid had nothing to strike (mo-bundle-preis).
    // Its amount is copied from the regular one, so both stay in Liquid's money format.
    preis.querySelector('.price-item--sale').textContent =
      preis.querySelector('.price__regular .price-item').textContent;
    preis.querySelector('.price__compare s').textContent = geld(streichGesamt);
    preis.classList.add('price--on-sale');

    // The label is a legal requirement for this struck price (MO-147).
    const vergleich = preis.querySelector('.price__compare');
    vergleich.append(el('span', 'mo-bundle__compare-label', texte.summe_einzelpreise));
    // .price__sale stacks in reverse, so this sits between struck and bundle price.
    vergleich.before(el('dd', 'mo-bundle__savings', ersetze(texte.ersparnis, { w: geld(streichGesamt - bundle.preis) })));
  }

  // MO counts the free extras in as well (MO-139).
  const setAnzahl = wurzel.querySelector('[data-mo-set-anzahl]');
  if (setAnzahl) {
    setAnzahl.textContent = ersetze(texte.set_anzahl, { n: bundle.positionen.reduce((s, p) => s + p.menge, 0) });
    setAnzahl.hidden = false;
  }

  const gratis = bundle.positionen.filter((p) => p.preis === 0);
  const platz = wurzel.querySelector('[data-mo-gratis]');
  if (!platz || !gratis.length) return;
  const rahmen = el('div', 'mo-bundle__free-wrapper');
  rahmen.append(el('p', 'mo-bundle__free-title', `${texte.gratis}:`));
  const liste = el('ul', 'mo-bundle__free-list');
  for (const p of gratis) {
    const text = el('span', 'mo-bundle__free-text');
    text.append(el('span', 'mo-bundle__free-name', p.titel));
    const wertzeile = el('span', 'mo-bundle__free-worth');
    wertzeile.append(el('span', 'mo-bundle__free-quantity', ersetze(texte.anzahl, { n: p.menge })));
    const wert = (p.nennwert ?? 0) * p.menge;
    if (wert > 0) wertzeile.append(el('span', 'mo-bundle__free-value', ersetze(texte.wert, { w: geld(wert) })));
    text.append(wertzeile);
    const li = el('li', 'mo-bundle__free-line');
    li.append(text);
    liste.append(li);
  }
  rahmen.append(liste);
  platz.replaceWith(rahmen);
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('mo-bundle-stufe-live')) {
  class MoBundleStufeLive extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.moStatus) return;
      this.dataset.moStatus = 'laedt';
      const texte = JSON.parse(this.dataset.texte || '{}');
      const geld = (cents) => new Intl.NumberFormat(this.dataset.locale || 'de-DE', {
        style: 'currency', currency: this.dataset.currency || 'EUR',
      }).format(cents / 100);

      try {
        const bundle = await ladeBundleEinmal(this.dataset.handle, {
          apiVersion: this.dataset.api, token: this.dataset.token,
          routesRoot: window.Shopify?.routes?.root ?? '/',
        });
        if (!bundle.positionen.length) throw new BundleLadefehler('bundle has no components');
        if (this.dataset.form === 'bilder') {
          const ziel = this.querySelector('[data-mo-ziel]');
          bilderFuellen(ziel, bundle.positionen, texte);
          ziel.removeAttribute('aria-busy');
        } else {
          kompaktFuellen(this, bundle, texte, geld);
        }
        this.dataset.moStatus = 'fertig';
      } catch (fehler) {
        console.warn('[mo-bundle-stufe-live]', this.dataset.form, this.dataset.handle, fehler);
        this.dataset.moStatus = 'fehler';
        const zeilen = this.querySelector('[data-mo-zeilen]');
        if (zeilen && !zeilen.children.length && texte.fehler) {
          zeilen.after(el('p', 'mo-hinweis', texte.fehler));
        }
      }
    }
  }

  customElements.define('mo-bundle-stufe-live', MoBundleStufeLive);
}
